import { useEffect, useState } from "react";

import { SpotlightSearch } from "@/components/CasesList/components/SpotlightSearch";
import { DataTablePagination } from "@/components/DataTables/DataTablePagination";
import { PageHeader } from "@/components/PageHeader";
import { UserIdentity } from "@/components/UserIdentity";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { type AuditLogRow, useAuditLog } from "@/hooks/queries/use-audit-log";

import { Filter } from "./components/Filter";
import { ACTION_BADGE_VARIANT, ACTION_LABELS, USER_TYPE_LABELS } from "./constants";

/**
 * Admin-only Report Audit Log (MDRS-271, widened by MDRS-455).
 *
 * Surfaces every report-related activity event — finalise, unlock, admin PDF
 * download, marked-uploaded-to-Vinci, doctor/admin Word download, and PDF
 * preview — not just the finalisation chain-of-custody subset this page
 * originally showed. Route is wrapped in `<AdminRoute>`.
 *
 * Real server-side pagination (not a client-side slice of one big fetch):
 * downloads/previews are per-click events, orders of magnitude more frequent
 * than the lifecycle actions, so the page can no longer assume the whole log
 * fits in one request.
 *
 * Action/user-type filters live in the `Filter` popover (matching CasesList's
 * and BookingApprovals's Filter trigger) — the free-text search stays inline,
 * same split as those two pages.
 */

/** Debounce a changing value by `delay` ms — avoids a request per keystroke. */
const useDebounced = <T,>(value: T, delay = 300): T => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
};

/** The backend sends naive UTC timestamps (no "Z"); `new Date` would read those
 * as browser-local time, so treat an offset-less value as UTC (MDRS-502). */
const fmt = (iso: string | null): string => {
  if (!iso) return "—";
  const hasOffset = /(Z|[+-]\d{2}:?\d{2})$/i.test(iso);
  const d = new Date(hasOffset ? iso : `${iso}Z`);
  return isNaN(d.getTime()) ? iso : d.toLocaleString();
};

/** Actions whose backend `description` embeds a raw response UUID
 * (e.g. "Downloaded report .docx (response 8aa79234-...)") — not something an
 * admin reading the log should have to look at. The one useful bit of context
 * they carry is already broken out as `data.finalised`; show that instead. */
const RESPONSE_ID_DESCRIPTION_ACTIONS = new Set([
  "report_downloaded",
  "report_downloaded_admin",
  "report_previewed",
]);

const Details = ({ row }: { row: AuditLogRow }) => {
  if (row.action === "report_unlocked") {
    return (
      <div className="space-y-0.5 text-xs">
        <div>
          <span className="text-foreground-muted">Reason:</span> {row.reason || "—"}
        </div>
        {row.prior_finalised_at ? (
          <div className="text-foreground-muted">
            Prior finalised: {fmt(row.prior_finalised_at)}
          </div>
        ) : null}
      </div>
    );
  }

  if (RESPONSE_ID_DESCRIPTION_ACTIONS.has(row.action)) {
    const finalised = row.data?.finalised;
    const label =
      typeof finalised === "boolean"
        ? finalised
          ? "Finalised report"
          : "Draft report"
        : "—";
    return <span className="text-xs text-foreground-muted">{label}</span>;
  }

  return <span className="text-xs text-foreground-muted">{row.description}</span>;
};

export default function AuditLogPage() {
  const [search, setSearch] = useState("");
  const [userType, setUserType] = useState("");
  const [action, setAction] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const debouncedSearch = useDebounced(search);

  const { data, isLoading, error } = useAuditLog({
    search: debouncedSearch || undefined,
    userType: userType || undefined,
    action: action || undefined,
    page: currentPage,
    limit: pageSize,
  });

  const rows = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const hasActiveFilters = Boolean(search || userType || action);

  return (
    <div className="space-y-2 p-2 pl-0">
      <div className="rounded-2xl border border-foreground-muted/20 bg-background-white p-4">
        <PageHeader
          title="Audit Log"
          description="Every finalise, download, preview, and unlock across reports — search and filter to find what you need."
        />
      </div>

      <div className="overflow-hidden rounded-2xl border border-foreground-muted/20 bg-background-white">
        <div className="flex flex-wrap items-center justify-between gap-3 p-4">
          <div className="flex items-center gap-2">
            <h2 className="text-base font-semibold tracking-[-0.01em]">Events</h2>
            <Badge variant="secondary">{total}</Badge>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-[280px] shrink-0">
              <SpotlightSearch
                value={search}
                onChange={(v) => {
                  setSearch(v);
                  setCurrentPage(1);
                }}
                placeholder="Search email or case number"
              />
            </div>
            <Filter
              onFiltersChange={(filters) => {
                setAction(filters.action ?? "");
                setUserType(filters.userType ?? "");
                setCurrentPage(1);
              }}
            />
          </div>
        </div>

        <Table className="border-t border-foreground-muted/20">
          <TableHeader>
            <TableRow>
              <TableHead>When</TableHead>
              <TableHead>Who</TableHead>
              <TableHead>User Type</TableHead>
              <TableHead>Action</TableHead>
              <TableHead>Case</TableHead>
              <TableHead>Details</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={6} className="text-foreground-muted">
                  Loading…
                </TableCell>
              </TableRow>
            ) : error ? (
              <TableRow>
                <TableCell colSpan={6} className="text-destructive">
                  {(error as Error).message}
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-foreground-muted">
                  No audit activity{hasActiveFilters ? " for that filter" : " yet"}.
                </TableCell>
              </TableRow>
            ) : (
              rows.map((r) => (
                <TableRow key={r.id} className="hover:bg-[#E8FDFF]/50">
                  <TableCell className="text-xs">{fmt(r.created_at)}</TableCell>
                  <TableCell>
                    <UserIdentity name={r.actor_name} email={r.actor_email} />
                  </TableCell>
                  <TableCell className="text-xs text-foreground-muted">
                    {r.actor_user_type
                      ? (USER_TYPE_LABELS[r.actor_user_type] ?? r.actor_user_type)
                      : "—"}
                  </TableCell>
                  <TableCell>
                    <Badge variant={ACTION_BADGE_VARIANT[r.action] ?? "secondary"}>
                      {ACTION_LABELS[r.action] ?? r.action}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-xs">{r.case_number ?? "—"}</TableCell>
                  <TableCell className="whitespace-normal">
                    <Details row={r} />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>

        {total > 0 && (
          <DataTablePagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={total}
            pageSize={pageSize}
            onPageChange={setCurrentPage}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setCurrentPage(1);
            }}
            showSelectedCount={false}
          />
        )}
      </div>
    </div>
  );
}
